import type { MutationResult } from '../../api/contract';
import { createRepositoryToken } from '../../data/RepositoryProvider';
import type { PageResult } from '../../shared/data/types';
import type {
  ChangeGameStatusInput,
  ChangeGameTypeStatusInput,
  ChangePartnerGameStatusInput,
  CreateGameInput,
  CreateGameTypeInput,
  CreatePartnerGameInput,
  GameDetail,
  GameListQuery,
  GameSummary,
  GameTypeDetail,
  GameTypeListQuery,
  GameTypeSummary,
  PartnerGame,
  UpdateGameInput,
  UpdateGameTypeInput,
  UpdatePartnerGameInput,
} from './types';

export interface GamesRepository {
  listGameTypes(
    query: GameTypeListQuery,
    signal: AbortSignal,
  ): Promise<PageResult<GameTypeSummary>>;
  getGameType(gameTypeId: string, signal: AbortSignal): Promise<GameTypeDetail>;
  createGameType(input: CreateGameTypeInput): Promise<MutationResult<GameTypeDetail>>;
  updateGameType(
    gameTypeId: string,
    input: UpdateGameTypeInput,
  ): Promise<MutationResult<GameTypeDetail>>;
  changeGameTypeStatus(
    gameTypeId: string,
    input: ChangeGameTypeStatusInput,
  ): Promise<MutationResult<GameTypeDetail>>;
  listGames(query: GameListQuery, signal: AbortSignal): Promise<PageResult<GameSummary>>;
  getGame(gameId: string, signal: AbortSignal): Promise<GameDetail>;
  createGame(input: CreateGameInput): Promise<MutationResult<GameDetail>>;
  updateGame(gameId: string, input: UpdateGameInput): Promise<MutationResult<GameDetail>>;
  changeGameStatus(
    gameId: string,
    input: ChangeGameStatusInput,
  ): Promise<MutationResult<GameDetail>>;
  listPartnerGames(partnerId: string, signal: AbortSignal): Promise<readonly PartnerGame[]>;
  getPartnerGame(partnerId: string, gameId: string, signal: AbortSignal): Promise<PartnerGame>;
  createPartnerGame(
    partnerId: string,
    input: CreatePartnerGameInput,
  ): Promise<MutationResult<PartnerGame>>;
  updatePartnerGame(
    partnerId: string,
    gameId: string,
    input: UpdatePartnerGameInput,
  ): Promise<MutationResult<PartnerGame>>;
  changePartnerGameStatus(
    partnerId: string,
    gameId: string,
    input: ChangePartnerGameStatusInput,
  ): Promise<MutationResult<PartnerGame>>;
}

export const gamesRepositoryToken = createRepositoryToken<GamesRepository>('games');

export const defaultGameTypeListQuery: GameTypeListQuery = Object.freeze<GameTypeListQuery>({
  page: 1,
  pageSize: 25,
  sort: 'updatedAt',
  order: 'desc',
  filters: { code: '', name: '', status: '' as const },
});

export const defaultGameListQuery: GameListQuery = Object.freeze<GameListQuery>({
  page: 1,
  pageSize: 25,
  sort: 'updatedAt',
  order: 'desc',
  filters: { gameTypeId: '', code: '', name: '', status: '' as const },
});
